<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WinDealModal from '../WinDealModal.vue';
import LoseDealModal from '../LoseDealModal.vue';
import DealStageBar from './DealStageBar.vue';
import { getDealTotalValue } from '../dealValue';
import { dealActionErrorMessage } from '../dealActionError';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnel: {
    type: Object,
    default: () => ({}),
  },
  stages: {
    type: Array,
    default: () => [],
  },
  // Optional callback invoked after a mutation (move/won/lost/transfer/value
  // edit) succeeds, so the parent (DealRecordView) can refresh its data.
  // Kept as a simple prop/callback instead of an extra emit per action to
  // avoid a 1:1 emit-per-mutation surface.
  reload: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['back']);

const { t } = useI18n();
const store = useStore();

const contactName = computed(
  () => props.conversation?.meta?.sender?.name || ''
);
const companyName = computed(
  () =>
    props.conversation?.meta?.sender?.additional_attributes?.company_name || ''
);

const canMove = computed(
  () => props.funnel?.settings?.allow_manual_move !== false
);

const triggerReload = () => {
  props.reload?.();
  // Avisa o DealActionsPanel (lista própria de mappings) que funil/etapa
  // mudou por aqui (Ganhou/Perdeu/mover/transferir/reabrir).
  window.dispatchEvent(
    new CustomEvent('conversation-funnel-updated', {
      detail: { source: 'header' },
    })
  );
};

// --- Stage move -----------------------------------------------------------
const onMove = async stageName => {
  if (props.funnel?.settings?.allow_manual_move === false) {
    useAlert(t('KANBAN.ERRORS.MANUAL_MOVE_NOT_ALLOWED'));
    return;
  }
  try {
    await KanbanAPI.moveConversation(
      props.funnel.id,
      props.conversation.id,
      stageName
    );
    triggerReload();
  } catch (error) {
    useAlert(t('KANBAN.ERRORS.MOVE_FAILED'));
  }
};

// --- Won / Lost -------------------------------------------------------------
const showWinDealModal = ref(false);
const showLoseDealModal = ref(false);

// Mesma cadeia de valor do KanbanCard (mapping → deal → produtos), senão o
// modal de Ganho abre zerado quando o valor vem do catálogo de produtos.
const currentDealValue = computed(() =>
  getDealTotalValue(props.conversation, {
    mapping: props.mapping,
    funnelId: props.funnel?.id,
  })
);

const openWinDealModal = () => {
  showWinDealModal.value = true;
};
const openLoseDealModal = () => {
  showLoseDealModal.value = true;
};
const closeWinDealModal = () => {
  showWinDealModal.value = false;
};
const closeLoseDealModal = () => {
  showLoseDealModal.value = false;
};

const isMarkingDealWon = ref(false);
const handleWinDealConfirm = async data => {
  isMarkingDealWon.value = true;
  try {
    await KanbanAPI.markDealAsWon(props.funnel.id, data.conversationId, {
      final_value: data.finalValue,
      close_date: data.closeDate,
      notes: data.notes,
      won_reason: data.wonReason,
      won_reason_id: data.wonReasonId,
    });
    useAlert(t('KANBAN.DEAL.WIN.SUCCESS'));
    showWinDealModal.value = false;
    triggerReload();
  } catch (error) {
    useAlert(dealActionErrorMessage(error, t('KANBAN.DEAL.WIN.ERROR')));
  } finally {
    isMarkingDealWon.value = false;
  }
};

const handleLoseDealConfirm = async data => {
  try {
    await KanbanAPI.markDealAsLost(props.funnel.id, data.conversationId, {
      reason: data.reason,
      reason_id: data.reasonId,
      notes: data.notes,
    });
    useAlert(t('KANBAN.DEAL.LOSE.SUCCESS'));
    showLoseDealModal.value = false;
    triggerReload();
  } catch (error) {
    useAlert(dealActionErrorMessage(error, t('KANBAN.DEAL.LOSE.ERROR')));
  }
};

// --- More actions dropdown (transfer + reopen) ------------------------------
const showActionsDropdown = ref(false);
const toggleActionsDropdown = () => {
  showActionsDropdown.value = !showActionsDropdown.value;
};
const closeActionsDropdown = () => {
  showActionsDropdown.value = false;
};

const canReopen = computed(() =>
  ['won', 'lost'].includes(props.mapping?.deal_status)
);

const moreMenuItems = computed(() => {
  const items = [
    {
      action: 'transfer',
      value: 'transfer',
      label: t('DEAL_RECORD.HEADER.TRANSFER_FUNNEL'),
      icon: 'i-lucide-share-forward',
    },
  ];
  if (canReopen.value) {
    items.push({
      action: 'reopen',
      value: 'reopen',
      label: t('DEAL_RECORD.HEADER.REOPEN'),
      icon: 'i-lucide-refresh-cw',
    });
  }
  return items;
});

// --- Transfer to funnel (reuses KanbanView's flow/API) -----------------------
const transferDialogRef = ref(null);
const allFunnels = computed(() => store.getters['funnels/getFunnels'] || []);
const availableTargetFunnels = computed(() =>
  allFunnels.value.filter(f => f.id !== props.funnel?.id && f.active !== false)
);

const selectedTargetFunnelId = ref(null);
const selectedTargetStage = ref(null);
const removeFromSourceOnTransfer = ref(true);
const isTransferring = ref(false);

const selectedTargetFunnel = computed(() =>
  availableTargetFunnels.value.find(
    f => String(f.id) === String(selectedTargetFunnelId.value)
  )
);

const targetFunnelStages = computed(() => {
  if (!selectedTargetFunnel.value?.stages) return [];
  return Object.entries(selectedTargetFunnel.value.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([name, config]) => ({ name, ...config }));
});

const openTransferDialog = () => {
  selectedTargetFunnelId.value = null;
  selectedTargetStage.value = null;
  removeFromSourceOnTransfer.value = true;
  transferDialogRef.value?.open();
};

const confirmTransfer = async () => {
  if (!selectedTargetFunnel.value || !selectedTargetStage.value) return;
  isTransferring.value = true;
  try {
    await KanbanAPI.transferToFunnel(
      props.funnel.id,
      props.conversation.display_id,
      selectedTargetFunnel.value.id,
      selectedTargetStage.value,
      removeFromSourceOnTransfer.value
    );
    useAlert(
      t('KANBAN.TRANSFER.SUCCESS', {
        funnelName: selectedTargetFunnel.value.name,
      })
    );
    transferDialogRef.value?.close();
    triggerReload();
  } catch (error) {
    const msg = error?.response?.data?.error || t('KANBAN.TRANSFER.ERROR');
    useAlert(msg);
  } finally {
    isTransferring.value = false;
  }
};

const reopenDeal = async () => {
  try {
    await KanbanAPI.reopenDeal(props.funnel.id, props.conversation.id);
    useAlert(t('DEAL_RECORD.HEADER.REOPEN_SUCCESS'));
    triggerReload();
  } catch (error) {
    useAlert(t('DEAL_RECORD.HEADER.REOPEN_ERROR'));
  }
};

const handleMoreAction = ({ action }) => {
  closeActionsDropdown();
  if (action === 'transfer') openTransferDialog();
  if (action === 'reopen') reopenDeal();
};

const handleBack = () => emit('back');
</script>

<template>
  <div
    class="flex flex-col w-full gap-4 px-6 pt-4 pb-3 border-b border-n-weak"
    data-tour="deal-header"
  >
    <div class="flex items-center w-full gap-4">
      <div class="flex items-center gap-3 min-w-0 shrink-0">
        <Button
          icon="i-lucide-arrow-left"
          color="slate"
          variant="ghost"
          size="sm"
          :title="$t('DEAL_RECORD.HEADER.BACK')"
          @click="handleBack"
        />
        <Avatar :name="contactName || '?'" :size="40" rounded-full />
        <div class="flex flex-col min-w-0">
          <h1 class="text-lg font-semibold truncate text-n-slate-12">
            {{ contactName }}
          </h1>
          <span v-if="companyName" class="text-sm truncate text-n-slate-11">
            {{ companyName }}
          </span>
        </div>
      </div>

      <DealStageBar
        class="justify-center flex-1 min-w-0"
        :stages="stages"
        :current-stage="mapping.stage_name"
        :can-move="canMove"
        @move-to-stage="onMove"
      />

      <div class="flex items-center gap-2 shrink-0" data-tour="deal-acoes">
        <Button
          size="sm"
          color="teal"
          icon="i-lucide-trophy"
          :label="$t('KANBAN.DEAL.WIN.BUTTON')"
          @click="openWinDealModal"
        />
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-x-circle"
          :label="$t('KANBAN.DEAL.LOSE.BUTTON')"
          @click="openLoseDealModal"
        />
        <div v-on-click-outside="closeActionsDropdown" class="relative">
          <Button
            icon="i-lucide-ellipsis"
            color="slate"
            variant="ghost"
            size="sm"
            :title="$t('DEAL_RECORD.HEADER.MORE_ACTIONS')"
            @click="toggleActionsDropdown"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="moreMenuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full"
            @action="handleMoreAction"
          />
        </div>
      </div>
    </div>

    <WinDealModal
      :show="showWinDealModal"
      :conversation="conversation"
      :current-value="currentDealValue"
      :is-submitting="isMarkingDealWon"
      @close="closeWinDealModal"
      @confirm="handleWinDealConfirm"
    />
    <LoseDealModal
      :show="showLoseDealModal"
      :conversation="conversation"
      @close="closeLoseDealModal"
      @confirm="handleLoseDealConfirm"
    />

    <Dialog
      ref="transferDialogRef"
      type="edit"
      :title="$t('KANBAN.TRANSFER.TITLE')"
      :confirm-button-label="$t('KANBAN.TRANSFER.CONFIRM')"
      :cancel-button-label="$t('KANBAN.TRANSFER.CANCEL')"
      :disable-confirm-button="
        !selectedTargetFunnel || !selectedTargetStage || isTransferring
      "
      :is-loading="isTransferring"
      @confirm="confirmTransfer"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.TRANSFER.SELECT_FUNNEL') }}
          </label>
          <div class="relative w-full">
            <select
              v-model="selectedTargetFunnelId"
              class="w-full text-sm rounded-lg reset-base bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-weak px-3 py-2 appearance-none ltr:pr-9 rtl:pl-9 focus:ring-2 focus:ring-n-brand focus:outline-none"
            >
              <option :value="null" disabled>
                {{ $t('KANBAN.TRANSFER.CHOOSE_FUNNEL') }}
              </option>
              <option
                v-for="targetFunnel in availableTargetFunnels"
                :key="targetFunnel.id"
                :value="targetFunnel.id"
              >
                {{ targetFunnel.name }}
              </option>
            </select>
            <span
              class="absolute -translate-y-1/2 pointer-events-none top-1/2 ltr:right-3 rtl:left-3 i-lucide-chevron-down size-4 text-n-slate-10"
            />
          </div>
        </div>
        <div v-if="selectedTargetFunnel" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.TRANSFER.SELECT_STAGE') }}
          </label>
          <div class="relative w-full">
            <select
              v-model="selectedTargetStage"
              class="w-full text-sm rounded-lg reset-base bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-weak px-3 py-2 appearance-none ltr:pr-9 rtl:pl-9 focus:ring-2 focus:ring-n-brand focus:outline-none"
            >
              <option :value="null" disabled>
                {{ $t('KANBAN.TRANSFER.CHOOSE_STAGE') }}
              </option>
              <option
                v-for="stage in targetFunnelStages"
                :key="stage.name"
                :value="stage.name"
              >
                {{ stage.label || stage.name }}
              </option>
            </select>
            <span
              class="absolute -translate-y-1/2 pointer-events-none top-1/2 ltr:right-3 rtl:left-3 i-lucide-chevron-down size-4 text-n-slate-10"
            />
          </div>
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="removeFromSourceOnTransfer" type="checkbox" />
          {{ $t('KANBAN.TRANSFER.REMOVE_FROM_SOURCE') }}
        </label>
      </div>
    </Dialog>
  </div>
</template>
